import {Alert, CircularProgress, Divider, Grid, InputAdornment, Stack, TextField, Typography} from "@mui/material";
import Button from "@mui/material/Button";
import React, {ChangeEvent, useCallback, useEffect, useMemo, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import toast from "react-hot-toast";
import DialogAddress, {AddressFormData} from "../components-company-data/DialogAddress";
import ReplayIcon from '@mui/icons-material/Replay';
import {Icon} from "@iconify/react";
import {
    Client,
    ClientAddress,
    ClientAddressFields,
    ClientAddressTypes,
    ClientFields,
    ClientForm,
    ClientRelationshipInsert,
    ClientRelationshipInsertFields,
    ClientRelationshipTypeClassification,
    ClientType
} from "src/types/client/clientData";
import AddressFormContentData from "../AddressFormContentData";
import {SolicitationClient, SolicitationClientFields} from "../../../../../types/solicitation/solicitationData";
import {DNISchema, NotRequiredSchema, RequiredSchema} from "src/utils/validations";
import {MaritalStates, Modules} from "../../../../../types/generalData";
import {HttpSolicitationClient} from "../../../../../http/solicitation/httpSolicitation";
import ClientSummaryDialogCard
    from "../../../../../@core/layouts/components/horizontal/app-bar-content/ClientSummaryDialogCard";
import {AsyncSelect, ControlledDatePicker, ControlledTextField} from "../../../../../components/forms";
import {SolicitationClientFormData, SolicitationClientFormDataFields} from "../../StepSuretiesData";
import {LoaderBlockUI} from "../../../../../components/loader";
import {HttpClient, HttpClientAddress, HttpClientRelationships} from "../../../../../http/client/httpClient";
import {EntityWithIdFields} from "../../../../../types/baseEntities";
import {HttpCacheClient} from "../../../../../http/cache/client/httpCacheClient";
import {ClientRelationshipClassificationCodes, ClientRelationshipCodes} from "../../../../../types/client/clientEnums";


interface SuretiesDetailDialogFormProps {
    client: Client,
    onFinishSave: () => void,
    solicitationId?: number,
    onBack: () => void,
    clientToEdit?: SolicitationClient,
    coOwners?: boolean,
}

const SuretiesDetailDialogForm = ({client, onFinishSave, solicitationId, onBack, clientToEdit, coOwners = false} : SuretiesDetailDialogFormProps) => {
    const [value, setValuePerc] = useState<number>(clientToEdit ? clientToEdit[SolicitationClientFields.Participation] : 0);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState<boolean>(false)
    const [editAddress, setEditAddress] = useState<boolean>(false)
    const [addressInDialog, setAddressInDialog] = useState<ClientAddress | undefined>(undefined)
    const [fiance, setFiance] = useState<Client | undefined>(undefined)
    const [loadingCuit, setLoadingCuit] = useState<boolean>(false)
    const [cuit, setCuit] = useState<string>('')
    const [relationshipTypes, setRelationshipTypes] = useState<ClientRelationshipTypeClassification[]>([]);

    const onCloseDialog = () => {
        setEditAddress(false)
        setAddressInDialog(undefined)
    }
    
    //TO DO: los datos de afip no se pueden sobreescribir, por ende lo de agregar un conyuge es medio confuso.
    //          en el update faltaria validar eso

    
    const ClientFormSchema = useMemo(() => yup.object().shape({
        [ClientFields.IdentificationNumber]:
            client?.[SolicitationClientFields.ClientTypeCode] === ClientType.Fisica
                ? DNISchema.required('Campo obligatorio')
                : NotRequiredSchema,
        [ClientRelationshipInsertFields.RelationshipTypeCode]: coOwners ? RequiredSchema.required('Campo Obligatorio') : NotRequiredSchema,
        [ClientFields.AfipResponsibilityTypeCode]: RequiredSchema.required("Campo Obligatorio"),
        [ClientFields.MaritalStatusCode]: client?.[SolicitationClientFields.ClientTypeCode] === ClientType.Fisica ? RequiredSchema.required("Campo Obligatorio") : NotRequiredSchema
    }), [client])

    const methods = useForm<SolicitationClientFormData>({
        resolver: yupResolver(ClientFormSchema),
        defaultValues: {
            [ClientFields.CUIT]: client[ClientFields.CUIT],
            [ClientFields.BusinessName]: client[ClientFields.BusinessName],
            [ClientFields.MaritalStatusCode]: client[ClientFields.MaritalStatusCode] || clientToEdit?.[SolicitationClientFields.MaritalStatusCode],
            [ClientFields.AfipResponsibilityTypeCode]: client[ClientFields.AfipResponsibilityTypeCode],
            [ClientFields.IdentificationNumber]: client[ClientFields.IdentificationNumber],
            [ClientFields.ClientClassificationCode]: client[ClientFields.ClientClassificationCode],
            [ClientFields.GenderCode]: client[ClientFields.GenderCode],
            [ClientFields.BirthDate]: client[SolicitationClientFields.ClientTypeCode] === ClientType.Fisica ? new Date(client[ClientFields.BirthDate]) : undefined,
            [SolicitationClientFormDataFields.Participation]: clientToEdit?.[SolicitationClientFields.Participation],
            [SolicitationClientFormDataFields.FianceCuit]: clientToEdit?.[SolicitationClientFields.FianceCuit],
            [SolicitationClientFormDataFields.FianceName]: clientToEdit?.[SolicitationClientFormDataFields.FianceName],
            [ClientRelationshipInsertFields.RelationshipTypeCode]: clientToEdit?.[SolicitationClientFields.RelationshipTypeCode]
        }
    })
    
    const watchMaritalStatus = methods.watch(ClientFields.MaritalStatusCode)
    const loadAddresses = () => {
        setLoading(true)
        HttpClientAddress.getByIdClient(client[EntityWithIdFields.Id]).then((r) => {
            const actualAddress = client[ClientFields.ClientTypeCode] === ClientType.Juridica ?
                r.find((a) => a[ClientAddressFields.AddressTypeCode] === ClientAddressTypes.Legal)
                :
                r.find((a) => a[ClientAddressFields.AddressTypeCode] === ClientAddressTypes.Real)

            methods.setValue(SolicitationClientFormDataFields.Address, actualAddress as AddressFormData)
        }).finally(() => setLoading(false))
    }
    const onSubmitDialog = () => {
        toast.success("La dirección fue guardada con éxito")
        loadAddresses()
    }

    useEffect(() => {
        if (clientToEdit && (clientToEdit[SolicitationClientFields.MaritalStatusCode] === MaritalStates.Casado || clientToEdit[SolicitationClientFields.MaritalStatusCode] === MaritalStates.Convivientes)) {
            setLoadingCuit(true)
            HttpClient.sincronizeClientByCUIT(clientToEdit[SolicitationClientFields.FianceCuit], Modules.SolicitationOnboarding).then((r) => {
                setLoadingCuit(false)
                if (!r.tieneError) {
                    setFiance(r.data)
                } else {
                    toast.error(r.descripcionError)
                }
            })
        }
        
        if (client && !clientToEdit && (client[ClientFields.MaritalStatusCode] === MaritalStates.Casado || client[ClientFields.MaritalStatusCode] === MaritalStates.Convivientes)) {
            setLoadingCuit(true)
            HttpClient.sincronizeClientByCUIT(client[ClientFields.PartnerCuit], Modules.SolicitationOnboarding).then((r) => {
                if (!r.tieneError) {
                    setFiance(r.data)
                } else {
                    toast.error(r.descripcionError)
                }
            }).finally(() => setLoadingCuit(false))
        }
    }, [client, clientToEdit]);
    
    const onSubmit = async (data: SolicitationClientFormData) => {
        const clientData: ClientForm = {
                ...client,
                ...data
        }
        await HttpClient.update(clientData)
        
        const promises = []
        
        if (client && solicitationId){
            const relacionCotitular: ClientRelationshipInsert[] = [
                {
                    [ClientRelationshipInsertFields.Participation]: data[SolicitationClientFormDataFields.Participation] || 0,
                    [ClientRelationshipInsertFields.RelationshipTypeCode]: coOwners ? (data[ClientRelationshipInsertFields.RelationshipTypeCode] || 0) : ClientRelationshipCodes.Sureties,
                    [ClientRelationshipInsertFields.RelationshipClassificationTypeCode]: coOwners ? ClientRelationshipClassificationCodes.CoOwners : ClientRelationshipClassificationCodes.MoreSureties,
                    [ClientRelationshipInsertFields.Signer]: false
                }]
            
                promises.push(HttpSolicitationClient.insertClientRelated(solicitationId, client[EntityWithIdFields.Id], client[ClientFields.CUIT],
                client[ClientFields.BusinessName], relacionCotitular))
        } 
        
        
        if (fiance && fiance[ClientFields.ClientTypeCode] === ClientType.Fisica && (watchMaritalStatus === MaritalStates.Casado || watchMaritalStatus === MaritalStates.Convivientes)) {
            const relacionConyuge: ClientRelationshipInsert[] = [
                {
                    [ClientRelationshipInsertFields.Participation]: 0,
                    [ClientRelationshipInsertFields.RelationshipTypeCode]: ClientRelationshipCodes.Fiance,
                    [ClientRelationshipInsertFields.RelationshipClassificationTypeCode]: ClientRelationshipClassificationCodes.RelatedPeople,
                    [ClientRelationshipInsertFields.Signer]: false
                }
            ]
            
            promises.push(HttpClientRelationships.postRelacion(client[EntityWithIdFields.Id], fiance[EntityWithIdFields.Id], relacionConyuge))
        }

        if (data[SolicitationClientFormDataFields.Address]) {
            const addressToInsert: AddressFormData = {
                ...data[SolicitationClientFormDataFields.Address],
                [ClientAddressFields.AddressTypeCode]: client[ClientFields.ClientTypeCode] === ClientType.Fisica ? ClientAddressTypes.Real : ClientAddressTypes.Legal,
                [ClientAddressFields.CountryCode]: 1,
                [ClientAddressFields.Verified]: true
            }

            // @ts-ignore
            !addressToInsert[ClientAddressFields.Id]
                ? promises.push(HttpClientAddress.insert(client[EntityWithIdFields.Id], addressToInsert as ClientAddress))
                : promises.push(HttpClientAddress.update(client[EntityWithIdFields.Id], addressToInsert as ClientAddress))
        }
        
        setLoading(true)
        
        Promise.all(promises).then(() => {
            toast.success('El cliente se guardó correctamente')
            onFinishSave()
        }).catch(() => toast.error('Ocurrió un error al guardar el cliente')).finally(() => setLoading(false))
    }
    
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        setValuePerc(parseInt(inputValue));

        if (parseInt(inputValue) === 0 || client?.[ClientFields.ClientTypeCode] === ClientType.Juridica || parseInt(inputValue)<= 100) {
            setError('');
        } else {
            setError('Número grande');
        }
    };
    
    useEffect(() => {
        methods.setValue(SolicitationClientFormDataFields.Participation, value)
    }, [value])

    const loadGenderTypes = useCallback(() => {
        return HttpCacheClient.getGeneros();
    }, [])

    const loadMaritalStatusTypes = useCallback(() => {
        return HttpCacheClient.getEstadosCiviles();
    }, [])

    const loadResponsibilityTypes = useCallback(() => {
        return HttpCacheClient.getResponsableTipo();
    }, [])

    const loadClientClassifications = useCallback(() => {
        return HttpCacheClient.getClienteClasificaciones()
    }, [])

    const loadRelationshipTypes = useCallback(() => {
        HttpCacheClient.getRelacionTipos(5, client[ClientFields.ClientClassificationCode])
            .then(relationshipList => {
                setRelationshipTypes(relationshipList);
            })
    }, [])
    
    useEffect(() => {
        loadAddresses()
    }, [client])
    
    useEffect(() => {
        coOwners && loadRelationshipTypes()
    }, [coOwners])

    const handleChangeCuit = (e: ChangeEvent<HTMLInputElement>) => {
        setCuit(e.target.value)
    }
    
    const onCancel = () => setCuit('')

    const handleClick = () => {
        setLoadingCuit(true)
        HttpClient.sincronizeClientByCUIT(cuit, Modules.SolicitationOnboarding).then((r) => {
            setLoadingCuit(false)
            if (!r.tieneError) {
                setFiance(r.data)
            } else {
                toast.error(r.descripcionError)
            }
        }).catch(() => {
            toast.error('El CUIT/CUIL ingresado es inválido')
            setLoadingCuit(false)
        })
    }
    
    
    return (
            <Grid item xs={12} container spacing={2}>
                <Grid item xs={12}>
                    <Typography fontSize={16} fontWeight={600}>Datos del fiador</Typography>
                </Grid>
                <Grid item xs={12}>
                    <Divider/>
                </Grid>
                <Grid item xs={12} mb={3}><ClientSummaryDialogCard client={client}/></Grid>
                            <Grid item xs={6}>
                                <AsyncSelect control={methods.control} label="Responsabilidad AFIP"
                                             defaultValue={client?.[ClientFields.AfipResponsibilityTypeCode]}
                                             loadOptions={loadResponsibilityTypes}
                                             name={ClientFields.AfipResponsibilityTypeCode}
                                             fullWidth
                                />
                            </Grid>
                            {
                                (client?.[ClientFields.ClientTypeCode] === ClientType.Fisica)
                                &&
                                <React.Fragment>
                                    <Grid item xs={2}>
                                        <ControlledTextField control={methods.control} label="Documento"
                                                             name={ClientFields.IdentificationNumber}
                                                             fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={2}>
                                        <AsyncSelect control={methods.control} label="Género" loadOptions={loadGenderTypes}
                                                     name={ClientFields.GenderCode}
                                                     fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={2}>
                                        <AsyncSelect control={methods.control} label="Estado Civil"
                                                     loadOptions={loadMaritalStatusTypes}
                                                     name={ClientFields.MaritalStatusCode}
                                                     fullWidth
                                        />
                                    </Grid>
                                    <Grid item md={3}>
                                            <ControlledDatePicker control={methods.control}
                                                                  label="Fecha Nacimiento"
                                                                  name={ClientFields.BirthDate}
                                            />
                                    </Grid>
                                </React.Fragment>
                            }
            
                            {
                                (client?.[ClientFields.ClientTypeCode] === ClientType.Juridica)
                                &&
                                <Grid item xs={6} container spacing={2}>
                                    <Grid item xs={6}>
                                        <AsyncSelect control={methods.control}
                                                     name={ClientFields.ClientClassificationCode}
                                                     defaultValue={client?.[ClientFields.ClientClassificationCode]}
                                                     label="Tipo Societario" loadOptions={loadClientClassifications}
                                                     fullWidth
                                        />
                                    </Grid>
            
                                    <Grid item xs={6}>
                                        <ControlledTextField name={SolicitationClientFormDataFields.Participation}
                                                             label='Participación'
                                                             control={methods.control}
                                                             fullWidth
                                                             value={value}
                                                             onChange={handleChange}
                                                             error={!!error}
                                                             helperText={error}
                                                             InputProps={{
                                                                 endAdornment: <InputAdornment position={'start'}>%</InputAdornment>
                                                             }}
                                        />
                                    </Grid>
                                </Grid>
                            }
                            {
                                coOwners &&
                                <Grid item xs={4}>
                                    <ControlledTextField control={methods.control}
                                                         style={{marginBottom: 0}}
                                                         label={"Tipo Relación"}
                                                         options={relationshipTypes}
                                                         select
                                                         name={ClientRelationshipInsertFields.RelationshipTypeCode}
                                                         fullWidth
                                    />
                                </Grid>
                            }
                            <Grid item xs={12} mt={5}>
                                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                    <Typography fontSize={16} fontWeight={600}>{`Domicilio ${client[ClientFields.ClientTypeCode] === ClientType.Fisica ? 'real' : 'legal'}`}</Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={12}>
                                <Divider/>
                            </Grid>
                            <Grid item xs={12} mt={1}>
                                {!loading &&
                                    <FormProvider {...methods}>
                                        <AddressFormContentData nameBase={SolicitationClientFormDataFields.Address}/>
                                    </FormProvider>
                                }
                            </Grid>
                                    {
                                        (watchMaritalStatus === MaritalStates.Casado || watchMaritalStatus === MaritalStates.Convivientes) &&
                                        <Grid item xs={12} container mt={2} spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography fontSize={16} fontWeight={600}>Datos del cónyuge</Typography>
                                            </Grid>
                                            <Grid item xs={12}><Divider/></Grid>
                                            <Grid item xs={12}>
                                                {
                                                    !fiance &&
                                                    <Grid item xs={12} justifyContent={'center'} textAlign={'center'}>
                                                        <Typography mt={2}>{'Ingrese el CUIT del conyuge para sincronizar los datos con AFIP y agilizar la operación'}</Typography>
                                                        <Stack direction={'row'} alignItems={'center'} spacing={4} mt={2} justifyContent={'center'}>
                                                            <TextField
                                                                value={cuit}
                                                                onChange={handleChangeCuit}
                                                                size='small'
                                                                label='CUIT/CUIL'
                                                            />
                                                            <Button color='primary' variant='contained' onClick={handleClick}>Validar</Button>
                                                        </Stack>
                                                        {
                                                            loadingCuit &&
                                                            <Stack alignItems={'center'} justifyContent={'center'} textAlign={'center'} pt={3} spacing={2}>
                                                                <CircularProgress color="primary" size={40} value={40} />
                                                                <Typography fontSize={14}>Aguarde mientras buscamos el CUIT/CUIL en la base de datos...</Typography>
                                                            </Stack>
                                                        }
                                                    </Grid>
                                                }
                                            </Grid>
                                            <Grid item xs={12}>
                                                {
                                                    fiance &&
                                                    (
                                                    fiance[ClientFields.ClientTypeCode] === ClientType.Fisica ?
                                                        <ClientSummaryDialogCard client={fiance}/>
                                                    :
                                                            <Alert severity={'error'}>No es posible cargar como cónyuge una empresa.</Alert>
                                                    )
                                                }
                                            </Grid>
                                            <Grid item xs={12} textAlign={'center'}>
                                                    {!loadingCuit && fiance && 
                                                                <Button color='primary' size='small' variant={'contained'} onClick={() => {
                                                                    setFiance(undefined)
                                                                    onCancel()
                                                                }} endIcon={<ReplayIcon />}>
                                                                    Recargar
                                                                </Button>
                                                    }
                                            </Grid>
                                        </Grid>
                                    }
                            <Grid item xs={12} mt={2}>
                                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                                    <Button color='secondary' variant='contained' onClick={onBack} startIcon={<Icon icon='mdi:chevron-left' fontSize={20} />}>Atrás</Button>
                                    <Button color='primary' variant='contained' onClick={methods.handleSubmit(onSubmit)}>
                                        Guardar
                                    </Button>
                                </Stack>
                            </Grid>
                            <DialogAddress
                                open={editAddress}
                                setEditAddress={setEditAddress}
                                renderAddressTitle={() => `Editar domicilio ${client[ClientFields.ClientTypeCode] === ClientType.Fisica ? 'real' : 'legal'}`}
                                address={addressInDialog && addressInDialog}
                                onClose={onCloseDialog}
                                onSubmitDialog={onSubmitDialog}
                                clientId={client[EntityWithIdFields.Id]}
                                addressType={client[ClientFields.ClientTypeCode] === ClientType.Fisica ? ClientAddressTypes.Real : ClientAddressTypes.Legal}
                            />
                {loading && <LoaderBlockUI />}
            </Grid>
    )
}


export default SuretiesDetailDialogForm